import {
  Checkbox,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  ChevronDown,
  RefreshCw,
  Search,
} from 'lift-ui';
import React, { useState, useTransition, useEffect, useRef } from 'react';

interface FilterOption {
  label: string;
  value: string;
  badge?: string; // Optional badge text to display next to the label
  badgeColor?: string; // Optional badge color (e.g., 'blue', 'green', 'purple')
}

interface FilterDropdownProps {
  selectedValues: string[];
  // eslint-disable-next-line no-unused-vars
  onSelectionChange: (values: string[]) => void;
  options: FilterOption[];
  placeholder?: string;
  // eslint-disable-next-line no-unused-vars
  getDisplayLabel?: (value: string) => string; // Function to get display label for a single selected value
  emptyMessage?: string; // Custom message to show when options array is empty
}

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  selectedValues,
  onSelectionChange,
  options,
  placeholder = 'All',
  emptyMessage,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [searchTerm, setSearchTerm] = useState('');
  const popoverContentRef = useRef<HTMLDivElement>(null);

  const handleSelectAll = () => {
    if (selectedValues.length === options.length) {
      // Deselecting all - use transition for better UX
      startTransition(() => {
        onSelectionChange([]);
      });
    } else {
      // Selecting all - use transition for better UX
      startTransition(() => {
        onSelectionChange(options.map(opt => opt.value));
      });
    }
  };

  const handleToggleValue = (value: string) => {
    if (selectedValues.includes(value)) {
      onSelectionChange(selectedValues.filter(v => v !== value));
    } else {
      onSelectionChange([...selectedValues, value]);
    }
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) {
      return placeholder || '';
    }
    if (selectedValues.length === options.length && options.length > 0) {
      return 'All Selected';
    }
    // Show generic label instead of count
    return 'Selected';
  };

  // Filter options based on search term
  const filteredOptions = options.filter(option => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      option.label.toLowerCase().includes(searchLower) ||
      option.badge?.toLowerCase().includes(searchLower)
    );
  });

  // Close dropdown when scrolling outside of it
  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = (event: Event) => {
      if (!popoverContentRef.current) return;

      // Safely get the target as a Node
      const target = event.target;
      if (!target || !(target instanceof Node)) return;

      // Check if the scroll happened inside the popover content or any of its descendants
      if (popoverContentRef.current.contains(target)) {
        return; // Scroll is inside the dropdown, don't close
      }

      // Also check if the scrolling element itself is the popover content or contains it
      const scrollingElement = event.currentTarget;
      if (
        scrollingElement &&
        scrollingElement instanceof Node &&
        popoverContentRef.current.contains(scrollingElement)
      ) {
        return; // Scroll is inside the dropdown, don't close
      }

      // Check if the scroll happened on an element that is a descendant of the popover
      let currentNode: Node | null = target;
      while (
        currentNode &&
        currentNode !== document.body &&
        currentNode !== document.documentElement
      ) {
        if (popoverContentRef.current.contains(currentNode)) {
          return; // Scroll is inside the dropdown, don't close
        }
        currentNode = currentNode.parentNode;
      }

      // Scroll happened outside the dropdown, close it
      setIsOpen(false);
    };

    // Listen for scroll events on the document and all scrollable containers
    // Using capture phase to catch all scroll events
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  return (
    <TooltipProvider>
      <div className="flex items-center w-full">
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 px-2 py-1 text-sm text-gray-700 bg-transparent border-none rounded hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition-colors w-full"
            >
              <span className="flex-1 flex items-center min-w-0 overflow-hidden">
                {getDisplayText() ? (
                  <span className="text-gray-700 text-sm font-medium bg-white rounded-2xl px-2 truncate block max-w-full">
                    {getDisplayText()}
                  </span>
                ) : null}
              </span>
              <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-64 p-0"
            align="start"
            onOpenAutoFocus={(e: Event) => e.preventDefault()}
          >
            <div ref={popoverContentRef}>
              {options.length === 0 && emptyMessage ? (
                <div className="px-3 py-6 text-center text-sm text-gray-500">{emptyMessage}</div>
              ) : (
                <>
                  {/* Search Bar */}
                  <div className="p-2 border-b border-gray-200 sticky top-0 bg-white z-10">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none z-10" />
                      <input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setSearchTerm(e.target.value)
                        }
                        className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-gray-400"
                      />
                    </div>
                  </div>

                  <div className="max-h-[30vh] overflow-y-auto">
                    <div
                      className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      onClick={handleSelectAll}
                    >
                      <Checkbox
                        className="mr-2"
                        checked={
                          selectedValues.length === options.length && selectedValues.length > 0
                        }
                        onCheckedChange={(checked: boolean | 'indeterminate') => {
                          startTransition(() => {
                            if (checked === true) {
                              onSelectionChange(options.map(opt => opt.value));
                            } else {
                              onSelectionChange([]);
                            }
                          });
                        }}
                      />
                      <span className="text-sm font-medium flex items-center gap-1">
                        All
                        {isPending && <RefreshCw className="h-3 w-3 text-blue-500 animate-spin" />}
                      </span>
                    </div>
                    {filteredOptions.length === 0 ? (
                      <div className="px-3 py-6 text-center text-sm text-gray-500">
                        No results found
                      </div>
                    ) : (
                      filteredOptions.map(option => (
                        <div
                          key={option.value}
                          className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleToggleValue(option.value)}
                        >
                          <Checkbox
                            className="mr-2"
                            checked={selectedValues.includes(option.value)}
                            onCheckedChange={(checked: boolean | 'indeterminate') => {
                              if (checked === true) {
                                onSelectionChange([...selectedValues, option.value]);
                              } else {
                                onSelectionChange(selectedValues.filter(v => v !== option.value));
                              }
                            }}
                          />
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="text-sm truncate flex-1">{option.label}</span>
                            {option.badge && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 max-w-[80px] truncate inline-block ${option.badgeColor === 'green'
                                      ? 'bg-green-100 text-green-700'
                                      : option.badgeColor === 'purple'
                                        ? 'bg-purple-100 text-purple-700'
                                        : option.badgeColor === 'orange'
                                          ? 'bg-orange-100 text-orange-700'
                                          : option.badgeColor === 'pink'
                                            ? 'bg-pink-100 text-pink-700'
                                            : option.badgeColor === 'indigo'
                                              ? 'bg-indigo-100 text-indigo-700'
                                              : option.badgeColor === 'teal'
                                                ? 'bg-teal-100 text-teal-700'
                                                : option.badgeColor === 'cyan'
                                                  ? 'bg-cyan-100 text-cyan-700'
                                                  : option.badgeColor === 'amber'
                                                    ? 'bg-amber-100 text-amber-700'
                                                    : 'bg-blue-100 text-blue-700'
                                      }`}
                                  >
                                    {option.badge}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>{option.badge}</TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </TooltipProvider>
  );
};
